import { Sun, Moon, Zap, MapPin, Coffee, Sunrise, Sunset, Clock } from "lucide-react";
import FormShell from "../components/FormShell";

const MORNING_OPTIONS = [
  { value: "early_bird", label: "Early bird", hint: "Up and exploring by 8am", icon: Sunrise },
  { value: "normal", label: "Easy start", hint: "No alarms — ready by 9:30", icon: Sun },
  { value: "sleep_in", label: "Sleep in", hint: "Mornings are sacred — 10:30+", icon: Clock },
];

const EVENING_OPTIONS = [
  { value: "early_evening", label: "Early evening", hint: "Wind down by 7pm", icon: Sunset },
  { value: "after_dinner", label: "After dinner", hint: "Enjoy the night until 9:30", icon: Moon },
  { value: "night_owl", label: "Night owl", hint: "The city comes alive after dark", icon: Zap },
];

const PACE_OPTIONS = [
  { value: "intensive", label: "Pack it in", icon: Zap },
  { value: "balanced", label: "Balanced", icon: MapPin },
  { value: "relaxed", label: "Relaxed", icon: Coffee },
];

const sectionLabelStyle = { fontSize: 9, letterSpacing: "0.18em", color: "var(--zen-dim)", marginBottom: 10, display: "block" };

function OptionCard({ selected, onClick, icon: Icon, label, hint }) {
  return (
    <button onClick={onClick}
      className="w-full flex items-center gap-4 p-3.5 text-left transition-all duration-200"
      style={{
        background: selected ? "var(--zen-s2)" : "var(--zen-s1)",
        border: selected ? "2px solid var(--zen-accent)" : "2px solid var(--zen-border)",
        borderRadius: 4,
      }}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: selected ? "var(--zen-accent)" : "var(--zen-border)" }}>
        <Icon className="w-5 h-5" style={{ color: selected ? "#fff" : "var(--zen-sub)" }} />
      </div>
      <div>
        <div className="text-sm font-semibold" style={{ color: "var(--zen-text)" }}>{label}</div>
        {hint && <div className="text-xs mt-0.5" style={{ color: "var(--zen-sub)" }}>{hint}</div>}
      </div>
    </button>
  );
}

export default function StylePage({
  onNext, onBack, startOver,
  dayStartLabel, setDayStartLabel,
  dayEndLabel, setDayEndLabel,
  tripPace, setTripPace,
}) {
  const canContinue = dayStartLabel && dayEndLabel && tripPace;

  return (
    <FormShell
      step={4}
      photo="/photos/onboard-style"
      photoPos="50% 50%"
      photoCredit="Photo · Jakub Hałun · CC BY 4.0"
      eyebrow="HOW DO YOU TRAVEL?"
      bigWord="HOW?"
      title="How do you like to travel?"
      subtitle="We'll build your days around your natural rhythm"
      onBack={onBack}
      onStartOver={startOver}
      onContinue={onNext}
      continueDisabled={!canContinue}
    >
      {/* Mornings */}
      <div>
        <div className="font-mono-label" style={sectionLabelStyle}>Your mornings</div>
        <div className="flex flex-col gap-2">
          {MORNING_OPTIONS.map(opt => (
            <OptionCard key={opt.value}
              selected={dayStartLabel === opt.value}
              onClick={() => setDayStartLabel(opt.value)}
              icon={opt.icon} label={opt.label} hint={opt.hint} />
          ))}
        </div>
      </div>

      {/* Evenings */}
      <div>
        <div className="font-mono-label" style={sectionLabelStyle}>Your evenings</div>
        <div className="flex flex-col gap-2">
          {EVENING_OPTIONS.map(opt => (
            <OptionCard key={opt.value}
              selected={dayEndLabel === opt.value}
              onClick={() => setDayEndLabel(opt.value)}
              icon={opt.icon} label={opt.label} hint={opt.hint} />
          ))}
        </div>
      </div>

      {/* Pace */}
      <div>
        <div className="font-mono-label" style={sectionLabelStyle}>Your pace</div>
        <div className="grid grid-cols-3 gap-3">
          {PACE_OPTIONS.map(opt => {
            const selected = tripPace === opt.value;
            const Icon = opt.icon;
            return (
              <button key={opt.value}
                onClick={() => setTripPace(opt.value)}
                className="py-4 px-2 flex flex-col items-center gap-2 transition-all duration-200"
                style={{
                  background: selected ? "var(--zen-accent)" : "var(--zen-s1)",
                  color: selected ? "#fff" : "var(--zen-text)",
                  border: selected ? "2px solid var(--zen-accent)" : "2px solid var(--zen-border)",
                  borderRadius: 4,
                }}>
                <Icon className="w-5 h-5" />
                <span className="text-xs font-semibold">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </FormShell>
  );
}
